// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import {
  handleDeleteMasterProduct,
  handleFetchMasterProducts,
} from 'apis/master_products';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { CustomizeItemBreadcrumbs, MasterProduct } from 'util/Types';
import DOMPurify from 'dompurify';
import { IconAddCircleOutline } from 'components/atoms/icons/IconAddCircleOutline';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import styles from './ProducerSiteDetailManagement.module.scss';
import { IconEdit } from 'components/atoms/icons/IconEdit';
import { IconDelete } from 'components/atoms/icons/IconDelete';
import CreateUpdateMasterProductModal from 'components/molecules/CreateUpdateMasterProductModal';
import DeleteItemModal from 'components/molecules/DeleteItemForm/DeleteItemModal';

type ProducerSiteDetailManagementProps = {
  onSetBreadcrumb: (breadcrumb: CustomizeItemBreadcrumbs) => void;
};

const ProducerSiteDetailManagement: React.FC<
  ProducerSiteDetailManagementProps
> = ({ onSetBreadcrumb: setBreadcrumb }) => {
  const { siteId } = useParams<{ siteId: string }>();
  const [data, setData] = useState<MasterProduct[]>([]);
  const [search, setSearch] = useState('');
  const [editingData, setEditingData] = useState<Partial<MasterProduct>>();
  const [deletingItem, setDeletingItem] = useState<MasterProduct>();
  const [siteName, setSiteName] = useState<string>();

  const { t } = useTranslation();

  const fetchMasterProducts = async () =>
    handleFetchMasterProducts({
      siteId,
      successCallback: (data) => {
        setBreadcrumb({
          operatorName: data.operatorName || '-',
          siteName: data.siteName || '-',
        });
        setData(data.masterProducts || []);
        setSiteName(data.siteName);
      },
    });

  useEffect(() => {
    fetchMasterProducts();
  }, [siteId]);

  const filteredData = useMemo(() => {
    if (!search.trim()) return data;

    const lowerSearch = search.trim().toLowerCase();

    return data.filter((item) => item.name.toLowerCase().includes(lowerSearch));
  }, [data, search]);

  return (
    <div className={styles.masterProducts}>
      <div
        className="count-list"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            t('common.page', {
              page: filteredData.length,
            }) ?? ''
          ),
        }}
      />

      <div className={`search-area ${styles.searchArea}`}>
        <div className="search-input">
          <input
            placeholder={t('common.placeholder.search') || '検索'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="icon">
            <IconSearch />
          </div>
        </div>

        <div className="button" onClick={() => setEditingData({})}>
          <IconAddCircleOutline />
          <span>製品を追加</span>
        </div>
      </div>

      <div className={`scroll-wrapper ${styles.scrollWrapper}`}>
        <table id={styles.masterProductTable}>
          <thead>
            <tr>
              <th className={styles.name}>製品</th>
              <th className={styles.action}>アクション</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.length ? (
              filteredData.map((item, i) => (
                <tr key={i}>
                  <td className={styles.name}>{item.name}</td>
                  <td className={styles.action}>
                    <div className={styles.contentAction}>
                      <div className={styles.actionEdit}>
                        <IconEdit onClick={() => setEditingData(item)} />
                      </div>
                      <div className={styles.actionDelete}>
                        <IconDelete onClick={() => setDeletingItem(item)} />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="msg-error">
                  {t('messages.M_008')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateUpdateMasterProductModal
        data={editingData}
        onClose={() => setEditingData(undefined)}
        siteName={siteName}
        onUpdateCreateSuccess={fetchMasterProducts}
      />

      {deletingItem && (
        <DeleteItemModal
          item={deletingItem}
          onClose={() => setDeletingItem(undefined)}
          onHandleDelete={() =>
            handleDeleteMasterProduct({
              id: deletingItem.id,
              successCallback: () => {
                setDeletingItem(undefined);
                fetchMasterProducts();
              },
            })
          }
        />
      )}
    </div>
  );
};

export default ProducerSiteDetailManagement;
